import { Alert, Button, Descriptions, Table, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { CalendarOutlined, FileTextOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type { RegistryCase, RegistryRecord } from '../../api/registryRecordApi';
import type { Patient } from '../../api/patientApi';
import PhaseShell, { SectionCard, StatChip } from './PhaseShell';
import {
  SectionEmpty,
  groupBySourceDocument,
  registryDate as date,
  renderDocGroups,
} from './registryDocGroups';
import type { PhaseStatus } from './RegistryPhaseWorkspace';

const BASIS_LABELS: Record<string, string> = {
  HISTOLOGY: 'Histologic',
  IMAGING: 'Imagistic',
  CLINICAL: 'Clinic',
};

const CATEGORY_LABELS: Record<string, string> = {
  MALIGNANT: 'malign',
  IN_SITU: 'in situ',
  BORDERLINE: 'borderline',
  BENIGN: 'benign',
  METASTASIS: 'metastază',
  UNKNOWN: 'nedeterminat',
};

const VALIDATION_LABELS: Record<string, string> = {
  VALIDATED: 'Validat',
  PENDING: 'În așteptare',
};

interface Props {
  /** ex. „Faza 4 — Registru & Follow-up” */
  title: string;
  record: RegistryRecord;
  patient: Patient | null;
  status: PhaseStatus;
}

/** Faza 4 (comună hepato/dermato): diagnosticul de registru + vizitele de control. */
export default function FinalRegistryPhasePage({ title, record, patient, status }: Props) {
  const navigate = useNavigate();
  const followUps = record.encounters
    .filter((e) => e.type === 'AMBULATORY_LETTER')
    .sort((a, b) => (a.startDate ?? '').localeCompare(b.startDate ?? ''));

  return (
    <PhaseShell
      title={title}
      description="Înregistrarea în registru și monitorizarea pacientului (follow-up)."
      status={status}
      chips={
        <>
          <StatChip count={record.cases.length} label="cazuri în registru" />
          <StatChip count={followUps.length} label="controale documentate" />
        </>
      }
      extra={
        <Button size="small" icon={<FileTextOutlined />} onClick={() => navigate('/registry-reports')}>
          Vezi rapoartele de registru
        </Button>
      }
    >
      <SectionCard icon={<SafetyCertificateOutlined />} title="Diagnostic de registru">
        <Descriptions size="small" column={2} style={{ marginBottom: 12 }}>
          {patient?.registryNumber && (
            <Descriptions.Item label="Număr de registru">{patient.registryNumber}</Descriptions.Item>
          )}
          <Descriptions.Item label="Stare validare">
            {VALIDATION_LABELS[record.validationStatus ?? ''] ?? record.validationStatus ?? '—'}
          </Descriptions.Item>
        </Descriptions>
        {record.cases.length === 0 ? (
          <SectionEmpty />
        ) : (
          renderDocGroups(
            groupBySourceDocument(record.cases, record.documents, (c) => c.diagnosis?.derivedFromDocumentId ?? c.sourceDocumentId),
            (cases) => (
              <Table<RegistryCase>
                size="small"
                rowKey={(c) => c.lesionId ?? c.code}
                pagination={false}
                dataSource={cases}
                columns={[
                  { title: 'Caz', dataIndex: 'code', key: 'code' },
                  { title: 'Diagnostic', key: 'dx', render: (_, c) => c.diagnosis?.text ?? '—' },
                  {
                    title: 'ICD-10 / ICD-O-3',
                    key: 'codes',
                    render: (_, c) => [c.diagnosis?.icd10 ?? c.presumptiveIcd10, c.diagnosis?.icdO3Morphology].filter(Boolean).join(' · ') || '—',
                  },
                  { title: 'Bază', key: 'basis', render: (_, c) => (c.diagnosis?.basis ? BASIS_LABELS[c.diagnosis.basis] ?? c.diagnosis.basis : '—') },
                  {
                    title: 'Categorie',
                    key: 'cat',
                    render: (_, c) =>
                      c.diagnosis?.category ? (
                        <Tag color={c.diagnosis.category === 'MALIGNANT' ? 'red' : 'default'}>
                          {CATEGORY_LABELS[c.diagnosis.category] ?? c.diagnosis.category}
                        </Tag>
                      ) : (
                        '—'
                      ),
                  },
                  { title: 'Dată incidență', key: 'inc', render: (_, c) => date(c.diagnosis?.incidenceDate) },
                ]}
              />
            ),
          )
        )}
      </SectionCard>

      <SectionCard icon={<CalendarOutlined />} title="Follow-up (vizite de control)">
        {followUps.length === 0 ? (
          <>
            <SectionEmpty text="Niciun control documentat în import" />
            {record.cases.some((c) => c.diagnosis) && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 8 }}
                message="Pacientul nu are nicio vizită de control documentată în import — planificați follow-up-ul."
              />
            )}
          </>
        ) : (
          renderDocGroups(
            groupBySourceDocument(followUps, record.documents, (e) => e.sourceDocumentId),
            (items) => (
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={items}
                columns={[
                  { title: 'Data', dataIndex: 'startDate', key: 'd', render: (v: string | null) => date(v) },
                  { title: 'Secția / unitatea', dataIndex: 'unit', key: 'u', render: (v: string | null) => v ?? '—' },
                  { title: 'Diagnostic', dataIndex: 'diagnosisText', key: 'dx', render: (v: string | null) => v ?? '—' },
                  { title: 'ICD-10', key: 'icd', render: (_, e) => [e.principalIcd10, ...(e.secondaryIcd10 ?? [])].filter(Boolean).join(', ') || '—' },
                ]}
              />
            ),
          )
        )}
      </SectionCard>
    </PhaseShell>
  );
}
